import { useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { Briefcase, UsersThree, MagnifyingGlassPlus, ChatTeardrop } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { useLayout } from '../../contexts/LayoutContext';
import { useFeature } from '../../hooks/useFeature';
import apiService from '../../services/apiService';
import { useConversationRefresh } from '../../hooks/useConversationRefresh';

const NewChatButtons = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { navCollapsed, collapseNav } = useLayout();
  const [creating, setCreating] = useState(null);
  const { triggerRefresh } = useConversationRefresh();
  const { enabled: assessmentsEnabled } = useFeature('assessments_mode');
  const { enabled: salesEnabled } = useFeature('sales_roleplay');
  const { enabled: leadEnabled } = useFeature('lead_roleplay');

  const createConversation = async (type, methodology = 'real') => {
    const key = methodology === 'basic' ? 'assessment' : type;
    setCreating(key);
    try {
      const timestamp = new Date().toLocaleString();
      const title = type === 'sales'
        ? `Sales Conversation ${timestamp}`
        : methodology === 'basic'
          ? `Case Study ${timestamp}`
          : `Leadership ${timestamp}`;

      const response = await apiService.post(
        '/api/v1/conversations',
        {
          conversationType: type,
          methodology,
          title,
          user_id: user.uid,
          tenant_id: user.tenantId
        },
        user
      );

      if (response && response.success && response.data) {
        triggerRefresh();
        collapseNav();
        navigate(`/app/chat/${response.data.id}`);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Failed to create conversation. Please try again.');
    } finally {
      setCreating(null);
    }
  };

  if (navCollapsed) {
    return (
      <div className="new-chat-buttons collapsed">
        <NavLink
          to="/app/conversations"
          className={({ isActive }) => `new-chat-btn nav-link-btn icon-only ${isActive ? 'active' : ''}`}
          title="My Conversations"
        >
          <ChatTeardrop weight="light" size={18} />
        </NavLink>
        {salesEnabled && (
          <button
            className="new-chat-btn sales-btn icon-only"
            onClick={() => createConversation('sales')}
            disabled={creating === 'sales'}
            title="Sales"
          >
            <Briefcase weight="light" size={18} />
          </button>
        )}
        {leadEnabled && (
          <button
            className="new-chat-btn leadership-btn icon-only"
            onClick={() => createConversation('leadership')}
            disabled={creating === 'leadership'}
            title="Leadership"
          >
            <UsersThree weight="light" size={18} />
          </button>
        )}
        {assessmentsEnabled && (
          <button
            className="new-chat-btn assessment-btn icon-only"
            onClick={() => createConversation('leadership', 'basic')}
            disabled={creating === 'assessment'}
            title="Personal Case Study"
          >
            <MagnifyingGlassPlus weight="light" size={18} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="new-chat-buttons">
      <NavLink
        to="/app/conversations"
        className={({ isActive }) => `new-chat-btn nav-link-btn ${isActive ? 'active' : ''}`}
      >
        <span className="btn-icon"><ChatTeardrop weight="light" size={15} /></span>
        My Conversations
      </NavLink>
      {salesEnabled && (
        <button
          className="new-chat-btn sales-btn"
          onClick={() => createConversation('sales')}
          disabled={creating === 'sales'}
        >
          <span className="btn-icon"><Briefcase weight="light" size={15} /></span>
          {creating === 'sales' ? 'Creating...' : 'New Sales'}
        </button>
      )}
      {leadEnabled && (
        <button
          className="new-chat-btn leadership-btn"
          onClick={() => createConversation('leadership')}
          disabled={creating === 'leadership'}
        >
          <span className="btn-icon"><UsersThree weight="light" size={15} /></span>
          {creating === 'leadership' ? 'Creating...' : 'New Leadership'}
        </button>
      )}
      {assessmentsEnabled && (
        <button
          className="new-chat-btn assessment-btn"
          onClick={() => createConversation('leadership', 'basic')}
          disabled={creating === 'assessment'}
        >
          <span className="btn-icon"><MagnifyingGlassPlus weight="light" size={15} /></span>
          {creating === 'assessment' ? 'Creating...' : 'New Personal Case Study'}
        </button>
      )}
    </div>
  );
};

export default NewChatButtons;
